/*jshint esversion: 6 */
import React, {useEffect, useState} from "react";
import {ServerIntf, UserScope} from './ServerIntf';
import {Login} from './Login';
import Register from './Register';
import {Rules} from "./Rules";
import PasswordReset from "./PasswordReset";
import NonEgms from './NonEgms';
import Egms from './Egms';
import GameRecall from './GameRecall';
import GbbModal from './GbbModal';
import Leaderboard from './Leaderboard';
import Wagers from './Wagers';
import Users from './Users';
import ConfigPage from './ConfigPage';
import {NotificationContainer} from 'react-notifications';
import {ColorRing} from 'react-loader-spinner';
import { normalizePasswdDurationDays } from './userPasswordExpiry';
import './App.css';

const isAdminScope = (scope) => scope === UserScope.CLIENT_ADMIN || scope === UserScope.MIAI_ADMIN;
const isMiaiAdminScope = (scope) => scope === UserScope.MIAI_ADMIN;
const canViewWagersForScope = (admin, scope) => !!admin || scope === UserScope.MIAI_VIEWER;
const canViewUsersForScope = (admin, scope) => !!admin || isAdminScope(scope);
const canViewConfigForScope = (admin, scope) => !!admin && isMiaiAdminScope(scope);

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isWaiting, setIsWaiting] = useState(false);
    const [isPrecall, setIsPrecall] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userScope, setUserScope] = useState(null);
    const [playerESS, setPlayerESS] = useState('');
    const [rulesVisible, setRulesVisibility] = useState(false);
    const [egmsVisible, setEgmsVisibility] = useState(true);
    const [nonEgmsVisible, setNonEgmsVisibility] = useState(true);
    const [recallVisible, setRecallVisibility] = useState(true);
    const [wagersVisible, setWagersVisibility] = useState(false);
    const [usersVisible, setUsersVisibility] = useState(false);
    const [context, setContext] = useState({ Version: 'unknown', SHA: 'not set', PasswdDurationDays: 90 });
    const [recall, setRecall] = useState({FloorLocation: null, Games:[]});
    const [modalState, setModalState] = useState({ show: false, host: '', title: 'Download Host Report', modalType: ServerIntf.ModalType.REPORT });
    const [egms, setEgms] = useState([]);
    const [unallocated, setUnallocated] = useState([]);
    const [allocated, setAllocated] = useState([]);
    const [users, setUsers] = useState([]);
    const [ess, setESS] = useState('');
    const [path, setPath] = useState(window.location.pathname);

    useEffect(() => {
        let contextSubscriber = ServerIntf.SubscribeContext((v) => setContext(v));
        let waitingSubscriber = ServerIntf.SubscribeWaiting((v) => setIsWaiting(v));
        let precallSubscriber = ServerIntf.SubscribePrecall((v) => setIsPrecall(v));
        let essSubscriber = ServerIntf.SubscribeESS((v) => setESS(v));
        let egmsSubscriber = ServerIntf.SubscribeEgms((v) => setEgms(v));
        let recallSubscriber = ServerIntf.SubscribeRecall((v) => setRecall(v));
        let modalSubscriber = ServerIntf.SubscribeModal((v) => setModalState(v));
        let userSubscriber = ServerIntf.SubscribeUsers((v) => setUsers(v));
        let loginSubscriber = ServerIntf.SubscribeLogin((v) => {
            setIsLoggedIn(!!(v && v.success));
            setIsAdmin(!!(v && v.admin));
            setUserScope(Number.isInteger(v && v.scope) ? v.scope : null);
            setPlayerESS((v && v.playerESS) || '');
        });
        let wagerSubscriber = ServerIntf.SubscribeWagers((v) => {
            const isAssigned = (w) => {
                const hostname = (w?.Hostname || '').trim();
                const essVal = (w?.ESS || w?.Ess || '').trim();
                return hostname !== '' && essVal !== '';
            };

            setAllocated(v.filter(isAssigned));
            setUnallocated(v.filter((w) => !isAssigned(w)));
        });

        return function () {
            contextSubscriber.unsubscribe();
            loginSubscriber.unsubscribe();
            waitingSubscriber.unsubscribe();
            precallSubscriber.unsubscribe();
            essSubscriber.unsubscribe();
            egmsSubscriber.unsubscribe();
            recallSubscriber.unsubscribe();
            modalSubscriber.unsubscribe();
            wagerSubscriber.unsubscribe();
            userSubscriber.unsubscribe();
        }
    }, []);

    useEffect(() => {
        const onPopState = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const setRoute = (nextPath) => {
        if (window.location.pathname !== nextPath) {
            window.history.replaceState({}, '', nextPath);
            setPath(nextPath);
        }
    };

    const isConfigPath = path.toLowerCase() === '/config';
    const isNonEgmPlayer = userScope === UserScope.NON_EGM_PLAYER;
    const canViewWagers = canViewWagersForScope(isAdmin, userScope);
    const canViewUsers = canViewUsersForScope(isAdmin, userScope);
    const canViewConfig = canViewConfigForScope(isAdmin, userScope);
    const canEditUsers = isAdmin;

    useEffect(() => {
        if (!isLoggedIn) {
            ServerIntf.SetESS('');
            setEgmsVisibility(true);
            setNonEgmsVisibility(true);
            setRecallVisibility(true);
            setUsersVisibility(false);
            setWagersVisibility(false);
            return;
        }
        if (isConfigPath && !canViewConfig) {
            setRoute('/');
        }
        if (!canViewUsers) {
            setUsersVisibility(false);
        }
        if (!canViewWagers) {
            setWagersVisibility(false);
        }
        if (!isConfigPath && !isNonEgmPlayer && !usersVisible && !wagersVisible) {
            setEgmsVisibility(true);
            setNonEgmsVisibility(true);
            setRecallVisibility(true);
        }
        if (userScope === UserScope.NON_EGM_PLAYER) {
            setEgmsVisibility(false);
            setNonEgmsVisibility(false);
            setRecallVisibility(true);
            setWagersVisibility(false);
            setUsersVisibility(false);
            ServerIntf.SetESS(playerESS || '');
        }
    }, [isLoggedIn, isAdmin, userScope, playerESS, isConfigPath, isNonEgmPlayer, canViewConfig, canViewUsers, canViewWagers, usersVisible, wagersVisible]);
    return (
        <div id="App" className="App">
            <nav className="navbar navbar-dark mx-auto">
                <div className="navbar-left">
                    {!isLoggedIn && <a href="/" style={{textDecoration: 'none'}}><span className="navbar-brand abs">Grab Bag Bingo</span></a>}
                    {isLoggedIn && <button className="nav-link-button" onClick={() => {
                        if (isConfigPath) {
                            setRoute('/');
                        }
                        setEgmsVisibility(!isNonEgmPlayer);
                        setRecallVisibility(true);
                        setNonEgmsVisibility(!isNonEgmPlayer);
                        setWagersVisibility(false);
                        setUsersVisibility(false);
                        }}>Grab Bag Bingo</button>}
                </div>
                <div className="navbar-center">
                    <span className="navbar-text">{context.SHA}</span>
                </div>
                <div className="navbar-right">
                    {isLoggedIn && !isNonEgmPlayer && <button className="nav-link-button" onClick={ServerIntf.EnrollmentReport}>Enrollment Report</button>}
                    {isLoggedIn && !isNonEgmPlayer && <button className="nav-link-button" onClick={ServerIntf.AuditReport}>Audit Report</button>}
                    {canViewWagers && !isConfigPath && <button className="nav-link-button" onClick={() => {
                        setEgmsVisibility(false);
                        setRecallVisibility(false);
                        setNonEgmsVisibility(true);
                        setWagersVisibility(true);
                        setUsersVisibility(false);
                        ServerIntf.GetWagers();
                    }}>Wagers</button>}
                    {canViewUsers && !isConfigPath && <button className="nav-link-button" onClick={() => {
                        // Hide other sections when Users is active
                        setEgmsVisibility(false);
                        setRecallVisibility(false);
                        setNonEgmsVisibility(false);
                        setWagersVisibility(false);
                        setUsersVisibility(true);
                        ServerIntf.GetUsers();
                    }}>Users</button>}
                    <button className="nav-link-button" onClick={() => setRulesVisibility(true)}>Rules</button>
                    {isLoggedIn && <button className="nav-link-button" onClick={ServerIntf.SignOff}>SignOff</button>}
                </div>
            </nav>
            {isLoggedIn ? (
                <div className='container'>
                    {!isConfigPath && <>
                        <h3>Grab Bag Bingo Casino Management Tool</h3>
                        {!usersVisible && <>
                            {!isNonEgmPlayer && <Egms visible={egmsVisible} ess={ess} egms={egms} canDownloadReports={isLoggedIn && !isNonEgmPlayer}/>}
                            {isNonEgmPlayer ? (
                                <Leaderboard visible={true} players={egms} playerESS={playerESS}/>
                            ) : (
                                <NonEgms visible={nonEgmsVisible} ess={ess} players={egms} wagersVisible={wagersVisible}/>
                            )}
                            <GameRecall visible={recallVisible} ess={ess} recall={recall}/>
                            {!isNonEgmPlayer && <Wagers visible={wagersVisible} unallocWagers={unallocated} allocWagers={allocated}/>}
                        </>}
                        {usersVisible && <Users visible={usersVisible} users={users} passwdDurationDays={normalizePasswdDurationDays(context.PasswdDurationDays)} canEdit={canEditUsers}/>}
                    </>}
                    {isConfigPath && <ConfigPage/>}
                </div>
            ) : path === "/Register" ? (
                <Register/>
            ) : path === "/PasswordReset" ? (
                <PasswordReset/>
            ) : isPrecall ? (
                <div className='container'>
                    <div className='container'><GameRecall ess={'preview'} recall={recall}/></div>
                </div>
            ) : (
                <Login/>
            )}
            <ColorRing visible={isWaiting} height="80" width="80" ariaLabel="blocks-loading"
                       wrapperClass={"loading-ring"} colors={['#94f503', '#5ef327', '#16f607', '#15860e', '#849b87']}/>
            <GbbModal state={modalState}/>
            <Rules visible={rulesVisible} onhide={() => setRulesVisibility(false)}/>
            <NotificationContainer/>
        </div>
    );
}

export default App;

// <Management />
